import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import { OverlayTrigger, Tooltip } from "react-bootstrap";

//components 
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import GenericList from '../../../components/GenericList/GenericList';
import GenericFilter from '../../../components/GenericList/GenericFilter';

//elements
import Button from '../../../components/CustomButton/CustomButton';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';
import LoadingSpinner from '~/elements/LoadingSpinner';

//assets
import Delete from '../../../assets/img/deleting.svg';

const member_assignment_statuses = [
    { code: 'assigned', name: 'Assigned' },
    { code: 'unassigned', name: 'Unassigned' },
];

class ManagesMembersForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filters: { name: '', status: '', project: '' },
            appliedFilters: { name: '', status: '', project: '' },
            selectedIds: [],
            currentPageUnassignedIds: [], 
            currentPageRows: [],
            assignedCount: 0,
            unassignedCount: 0,
            selectedProject: null,
            loading: false,
            reloadKey: Date.now(),
            page: 0,
            isPageAddedOrEdited: false,
        };
    }

    handlePageChange = (page, isPageAddedOrEdited) => {
        this.setState({ page, isPageAddedOrEdited });
    };

    componentDidMount() {
        this.loadCounts();
    }

    componentDidUpdate(prevProps) {
        if (this.props.id !== prevProps.id) {
            this.loadCounts();
            this.setState({ reloadKey: Date.now(), selectedIds: [], selectedProject: null, page: 0, isPageAddedOrEdited: false });
        }
    }

    loadCounts = async () => {
        const { id, userStore, commonStore, t } = this.props;
        const { currentUser } = userStore;
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        if (!id) return;
        try {
            const res = await userStore.pmUserCount({ id , pmMembers });
            this.setState({ assignedCount: res.assigned || 0, unassignedCount: res.unassigned || 0 });
        } catch (error) {
            console.error(error);
            commonStore.addNotification(t(error.message || 'Something went wrong'), null, 'error');
        }
    };

    fetchRows = (params = {}) => {
        const { id, userStore } = this.props;
        const { appliedFilters } = this.state;
        const { currentUser } = userStore;
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        const options = {
            page: params.page || 0,
            pagesize: params.pagesize,
            name: appliedFilters.name || '',
            assigned: appliedFilters.status || 'all',
            projectId: appliedFilters.project || '', 
            pmMembers: pmMembers,
        };

        return userStore.getPmMembers(id, options).then(response => {
            const list = response.rows || [];
            const unassignedIds = list.filter(r => !r.is_assigned && r.user_id !== parseInt(id)).map(r => r.user_id);
            this.setState({ currentPageUnassignedIds: unassignedIds, currentPageRows: list });
            return response;
        });
    };

    setFilter = (field, value) => {
        this.setState(prev => ({ filters: { ...prev.filters, [field]: value } }));
    };

    onFilter = () => {
        this.setState(prev => ({
            appliedFilters: { ...prev.filters },
            reloadKey: Date.now(),
            selectedIds: [],
            currentPageUnassignedIds: [],
            currentPageRows: [],
            page: 0,
            isPageAddedOrEdited: false
        }));
    };

    updateStatusFilter = (status) => {
        const { appliedFilters } = this.state;
        const newStatus = appliedFilters.status === status ? '' : status;

        this.setState(prev => ({
            filters: { ...prev.filters, status: newStatus }
        }), this.onFilter);
    };

    toggleSelect = (id) => {
        this.setState(prev => {
            const selected = prev.selectedIds.includes(id) 
                ? prev.selectedIds.filter(x => x !== id) 
                : [...prev.selectedIds, id];
            return { selectedIds: selected };
        });
    };

    toggleSelectAll = () => {
        this.setState(prev => {
            const { currentPageUnassignedIds, selectedIds } = prev;
            const allSelected = currentPageUnassignedIds.length > 0 && 
                                currentPageUnassignedIds.every(id => selectedIds.includes(id));
            
            if (allSelected) {
                return { selectedIds: selectedIds.filter(id => !currentPageUnassignedIds.includes(id)) };
            } else {
                return { selectedIds: Array.from(new Set([...selectedIds, ...currentPageUnassignedIds])) };
            }
        });
    };

    assignSelected = async () => {
        const { selectedIds } = this.state;
        const { id, userStore, commonStore, t } = this.props;
        if (!selectedIds.length || !id) return;
        this.setState({ loading: true });
        try {
            await userStore.savePmMembers({ id, userIds: selectedIds });
            commonStore.addNotification(t('Saved'), null, 'success');
            this.setState({ reloadKey: Date.now(), selectedIds: [], currentPageUnassignedIds: [], currentPageRows: [], isPageAddedOrEdited: true }, () => this.loadCounts());
        } catch (err) {
            commonStore.addNotification(err.message || t('Error assigning members'), null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    unassignMember = async (userId) => {
        const { id, userStore, commonStore, t } = this.props;
        if (userId === parseInt(id)) {
            return commonStore.addNotification(t('Cannot remove yourself'), null, 'warning');
        }

        try {
            confirmAlert({
                title: `${t('Confirm')} ?`,
                message: t('Are you sure you want to remove this member from the pm?'),
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: async () => {
                            const res = await userStore.deletePmMember(userId, id);
                            commonStore.addNotification(t(res.message) || t('Saved'), null, 'success');
                            this.setState({ reloadKey: Date.now() }, () => this.loadCounts());
                        },
                    },
                    {
                        label: t('No'),
                        onClick: () => { }
                    },
                ]
            })
           
        } catch (err) {
            commonStore.addNotification(err.message || t('Error removing member'), null, 'error');
        }
    };

    renderCheckboxCell = (row) => {
        const userId = row.original.user_id;
        const isAssigned = !!row.original.is_assigned;
        const isAssignedDirectly = !!row.original.is_assigned_directly;
        const { selectedIds } = this.state;
        const isSelected = selectedIds.includes(userId);
        const { id, t } = this.props;
        
        const isSelf = userId === parseInt(id);

        return (
            <div className="d-flex align-items-center justify-content-center">
                {(isAssigned || isSelf) ? (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id={`tooltip-remove-${userId}`}>
                                {isSelf
                                    ? t('Self')
                                    : !isAssignedDirectly
                                        ? t('Members are assigned through the project. To remove them from the PM’s members list, remove the PM or the user from the project.')
                                        : t('Remove member')
                                }
                            </Tooltip>
                        }
                    >
                        <span className="d-inline-block">
                            <Button
                                icon_sm
                                fill
                                disabled={isSelf || !isAssignedDirectly}
                                onClick={() => this.unassignMember(userId)}
                                style={{ pointerEvents: (isSelf || !isAssignedDirectly) ? 'disabled' : 'auto' }}
                            >
                                <img src={Delete} alt="delete" />
                            </Button>
                        </span>
                    </OverlayTrigger>
                ) : (
                    <Checkbox
                        inlineBlock
                        isChecked={isSelected}
                        onChange={() => this.toggleSelect(userId)}
                        wrapperClassName="checkbox-position-fix"
                        labelClassName="ps-0"
                    />
                )}
            </div>
        );
    }

    render() {
        const { id, t } = this.props;
        const { filters, appliedFilters, loading, selectedIds, currentPageUnassignedIds, assignedCount, unassignedCount } = this.state;

        if (!id) return <div />;
        if (loading) return <LoadingSpinner />;

        const isAllSelected = currentPageUnassignedIds.length > 0 &&
            currentPageUnassignedIds.every(id => selectedIds.includes(id));

        const columns = [
            {
                Header: t('Member'),
                id: 'first_name',
                accessor: 'first_name',
                textAlign : 'start',
                accessor: ({ first_name, last_name }) => <div>{first_name} {last_name}</div>,
            },
            {
                Header: `${t('Assigned')} ${t('Type')}`,
                id: 'type',
                textAlign: 'left',
                accessor: 'is_assigned_directly',
                Cell: ({ row }) => {
                    const { is_assigned, is_assigned_directly, user_id } = row.original;
                    const isSelf = user_id === parseInt(id);

                    if (!is_assigned && !isSelf) return <span className="text-muted">-</span>;
                    if (isSelf) return <span>{t('Self')}</span>;

                    const label = is_assigned_directly ? t('Direct') : t('Project');
                    const tooltip = is_assigned_directly
                        ? t('Member was assigned directly to the project manager')
                        : t('Member is assigned through a project');

                    return (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{tooltip}</Tooltip>}
                        >
                            <span className="cursor-pointer">
                                {label}
                            </span>
                        </OverlayTrigger>
                    )
                }
            },
            {
                Header: appliedFilters.status === 'assigned' ? t('Unassign') : appliedFilters.status === 'unassigned' ? t('Assign') : `${t('Assign')} / ${t('Unassign')}`,
                id: 'assigned',
                textAlign: 'center',
                accessor: 'user_id',
                Cell: (cellProps) => this.renderCheckboxCell(cellProps.row),
            }
        ];

        return (
            <div>
                <Row className="align-items-center mt-2 gx-2">
                    <Col xs={12} lg={8} xl={9}>
                        <Row className="member_status__summary">
                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-start">
                                <div
                                    className="highlightedgreen" role="button"
                                    onClick={() => this.updateStatusFilter('assigned')}
                                    style={{
                                        cursor: 'pointer',
                                        opacity: appliedFilters.status === 'assigned' ? 0.4 : 1
                                    }}
                                >
                                    {`${t('Assigned')} ${t('Members')}`} : {assignedCount}
                                </div>
                            </Col>
                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center">
                                <div
                                    className="highlightedred" role="button"
                                    onClick={() => this.updateStatusFilter('unassigned')}
                                    style={{
                                        cursor: 'pointer',
                                        opacity: appliedFilters.status === 'unassigned' ? 0.4 : 1
                                    }}
                                >
                                    {`${t('Unassigned')} ${t('Members')}`} : {unassignedCount}
                                </div>
                            </Col>
                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-end">
                                <Checkbox
                                    inlineBlock
                                    isChecked={isAllSelected}
                                    onChange={this.toggleSelectAll}
                                    disabled={currentPageUnassignedIds.length === 0}
                                    label={t("Select All")}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs="auto" className="d-flex justify-content-center justify-content-lg-end mt-2 mt-lg-0">
                        {selectedIds.length > 0 && (
                            <Button onClick={this.assignSelected} title={t('Assign')} fill>
                                {t('Assign')} ({selectedIds.length})
                            </Button>
                        )}
                    </Col>
                </Row>

                <div className='mb-3'>
                    <GenericList
                        key={this.state.reloadKey}
                        columns={columns}
                        header={
                            <Row>
                                <Col xs={12} sm={12} md={12} className={'mt-2'}>
                                    <GenericFilter
                                        filters={filters}
                                        setFilter={(name, value) => this.setFilter(name, value)}
                                        onFilter={() => this.onFilter()}
                                        loading={loading}
                                        hasStatusFilter={true}
                                        statusOptions={member_assignment_statuses}
                                        searchFilterPlaceholder={t('Search')}
                                        hasProjectFilter={true}
                                    />
                                </Col>
                            </Row>
                        }
                        requestData={(params) => this.fetchRows(params)}
                        filters={this.state.appliedFilters}
                        page={this.state.page}
                        isPageAddedOrEdited={this.state.isPageAddedOrEdited}
                        handlePageChange={this.handlePageChange}
                        showPageSizeSelector
                        pageSizeListId="pm_manage_members"
                    />
                </div>
            </div>
        );
    }
}

export default inject('userStore', 'commonStore')(
    applicationRouter(withLocalization(observer(ManagesMembersForm)))
);